import { AppLayout } from "@heroui-pro/react/app-layout";
import { Sidebar } from "@heroui-pro/react/sidebar";
import { useAppPathname as usePathname } from "@/lib/navigation/use-app-pathname";
import { useCallback, useState, type ReactNode } from "react";
import { Heading } from "react-aria-components";

import {
  normalizeApplicationPathname,
  resolveApplicationRoute,
  toStaticExportHref,
  type ApplicationMode,
} from "../../config/navigation";
import { useAppI18n } from "../../i18n/context";
import { useAppRouter } from "@/lib/navigation/use-app-router";
import { useNavigationPending } from "../feedback/navigation-pending";
import { useAccessSession } from "../providers/access-session";
import { workspaceRoleLabel } from "../../lib/presentation";
import { DashboardNavbar } from "./dashboard-navbar";
import { DashboardSidebar } from "./dashboard-sidebar";
import { DataTableTextSelectionGuard } from "./data-table-text-selection-guard";

export type AppShellProps = {
  mode: ApplicationMode;
  children: ReactNode;
};

/** HeroUI Pro 应用壳：侧栏导航 + 顶栏 + 内容区（前端静态导出适配）。 */
export function AppShell({ children, mode }: AppShellProps) {
  const { t } = useAppI18n();
  const pathname = usePathname();
  const router = useAppRouter();
  const { start: startNavigationPending } = useNavigationPending();
  const navigate = useCallback(
    (href: string) => {
      const next = toStaticExportHref(href);
      const sameRoute =
        normalizeApplicationPathname(next) ===
        normalizeApplicationPathname(pathname);
      // 同一路由键（仅斜杠/query）不开软导航，避免列表页 replace 再闪一次。
      if (!sameRoute) {
        startNavigationPending();
      }
      router.push(next);
    },
    [pathname, router, startNavigationPending],
  );
  const {
    me,
    membership,
    memberships,
    membershipReady,
    selectWorkspace,
    signOut,
    staleIsWorkspaceOwner,
    stalePermissions,
    staleWorkspaceStatus,
  } = useAccessSession();
  const profile = me?.profile;
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState<string | null>(null);
  const [failedAvatar, setFailedAvatar] = useState<string | null>(null);

  const isAdmin = mode === "admin";
  const currentRoute = resolveApplicationRoute(mode, pathname);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    setSignOutError(null);
    try {
      await signOut();
      router.replace(toStaticExportHref("/auth/login"));
    } catch {
      setSignOutError(t("shell.signOutFailed"));
    } finally {
      setIsSigningOut(false);
    }
  };

  const navigation = (
    <DashboardSidebar
      currentNavigationHref={currentRoute?.navigationHref}
      membership={
        membership
          ? {
              permissions: membership.permissions,
              role: membership.role,
              workspace: membership.workspace,
            }
          : undefined
      }
      membershipReady={membershipReady}
      memberships={memberships}
      mode={mode}
      onSelectWorkspace={selectWorkspace}
      staleIsWorkspaceOwner={staleIsWorkspaceOwner}
      stalePermissions={stalePermissions}
      staleWorkspaceStatus={staleWorkspaceStatus}
    />
  );

  const sidebar = (
    <>
      <Sidebar className="application-sidebar">
        {navigation}
        <Sidebar.Rail aria-label={t("shell.collapseSidebar")} />
      </Sidebar>
      <Sidebar.Mobile backdrop="blur" className="application-sidebar">
        <Heading className="sr-only" slot="title">
          {t(
            isAdmin
              ? "shell.platformNavigationLabel"
              : "shell.workspaceNavigationLabel",
          )}
        </Heading>
        {navigation}
      </Sidebar.Mobile>
    </>
  );

  const navbar = (
    <DashboardNavbar
      failedAvatar={failedAvatar}
      isAdmin={isAdmin}
      isSigningOut={isSigningOut}
      onAvatarError={() => {
        if (profile?.image) setFailedAvatar(profile.image);
      }}
      onSignOut={() => void handleSignOut()}
      accountRoleLabel={
        membership
          ? t("shell.accountRole", {
              role: workspaceRoleLabel(membership.role, t),
              workspace: membership.workspace.name,
            })
          : undefined
      }
      profileDisplayName={profile?.displayName}
      profileImage={profile?.image}
      signOutError={signOutError}
    />
  );

  return (
    <>
      <DataTableTextSelectionGuard />
      <AppLayout
        className="application-shell"
        defaultSidebarOpen
        navigate={navigate}
        navbar={navbar}
        scrollMode="content"
        sidebar={sidebar}
        sidebarCollapsible="icon"
        sidebarVariant="sidebar"
      >
        {children}
      </AppLayout>
    </>
  );
}

/** @deprecated 使用 `AppShell`；保留别名以降低过渡成本。 */
export const ApplicationShell = AppShell;
